import { ChevronsDownUp, ChevronsUpDown, Copy, WrapText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';

import { type JsonToolbarProps } from './JsonToolbar.types';

/**
 * Format, copy, and show-it-all.
 *
 * Copy survives read-only on purpose — copying is what a read-only value is
 * FOR, and a value nobody can take out of the page is a screenshot. Format does
 * not, because there would be nothing to write the result to.
 *
 * Expand is a labelled button rather than a resize handle: a dragged height and
 * an auto-growing one contradict each other, and a drag handle is not reachable
 * from a keyboard at all.
 *
 * Presentation only (U5).
 */
export function JsonToolbar({ onFormat, onCopy, onToggleExpanded, isExpanded }: JsonToolbarProps) {
  const { t } = useTranslation('uiKit');

  return (
    <>
      {onFormat === undefined ? null : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          startIcon={<WrapText aria-hidden className="h-4 w-4" />}
          onClick={onFormat}
        >
          {t('json.format')}
        </Button>
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        startIcon={<Copy aria-hidden className="h-4 w-4" />}
        onClick={onCopy}
      >
        {t('json.copy')}
      </Button>

      {onToggleExpanded === undefined ? null : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          startIcon={
            isExpanded ? (
              <ChevronsDownUp aria-hidden className="h-4 w-4" />
            ) : (
              <ChevronsUpDown aria-hidden className="h-4 w-4" />
            )
          }
          onClick={onToggleExpanded}
        >
          {isExpanded ? t('json.collapse') : t('json.expand')}
        </Button>
      )}
    </>
  );
}
